import { tokens } from '@fluentui/react-components';

/**
 * Fluent UI v9 Design Tokens
 * This file exports commonly used design tokens for easy access throughout the application.
 * All tokens are sourced from @fluentui/react-components to ensure consistency.
 */

// Color tokens - Use these for consistent theming
export const colorTokens = {
  // Brand colors
  brandPrimary: tokens.colorBrandBackground,
  brandSecondary: tokens.colorBrandBackground2,
  brandForeground: tokens.colorBrandForeground1,
  brandForeground2: tokens.colorBrandForeground2,

  // Neutral colors
  neutralBackground1: tokens.colorNeutralBackground1,
  neutralBackground2: tokens.colorNeutralBackground2,
  neutralBackground3: tokens.colorNeutralBackground3,
  neutralForeground1: tokens.colorNeutralForeground1,
  neutralForeground2: tokens.colorNeutralForeground2,
  neutralForeground3: tokens.colorNeutralForeground3,
  neutralForegroundOnBrand: tokens.colorNeutralForegroundOnBrand,
  neutralForegroundInverted: tokens.colorNeutralForegroundInverted,

  // Office application brand colors
  powerPointBrand: '#C43E1C', // PowerPoint orange primary
  powerPointBrandAlt: '#B1470E', // PowerPoint orange alternative (darker)
  wordBrand: '#185ABD', // Word blue primary
  wordBrandAlt: '#1F4788', // Word blue alternative
  excelBrand: '#107C41', // Excel green primary
  excelBrandAlt: '#0E5C2F', // Excel green alternative
  oneNoteBrand: '#7719AA', // OneNote purple primary
  oneNoteBrandAlt: '#5C1A87', // OneNote purple alternative
  formsBrand: '#03787C', // Forms teal primary
  formsBrandAlt: '#02605F', // Forms teal alternative (darker)
  formsBrandLight: '#04999E', // Forms teal lighter
  // Forms gradient for emphasis text
  formsBrandGradient: 'linear-gradient(90deg, #03787C 0%, #04999E 50%, #05B5BA 100%)',

  // Status colors
  successBackground: tokens.colorPaletteGreenBackground2,
  successForeground: tokens.colorPaletteGreenForeground2,
  warningBackground: tokens.colorPaletteYellowBackground2,
  warningForeground: tokens.colorPaletteYellowForeground2,
  errorBackground: tokens.colorPaletteRedBackground2,
  errorForeground: tokens.colorPaletteRedForeground2,

  // Stroke/border colors
  stroke1: tokens.colorNeutralStroke1,
  stroke2: tokens.colorNeutralStroke2,
  strokeAccessible: tokens.colorNeutralStrokeAccessible,

  // Selection/interaction states
  subtleBackgroundSelected: tokens.colorSubtleBackgroundSelected,
};

// Spacing tokens - Use these for consistent spacing
export const spacingTokens = {
  // Horizontal spacing
  spacingHorizontalNone: tokens.spacingHorizontalNone,
  spacingHorizontalXXS: tokens.spacingHorizontalXXS,
  spacingHorizontalXS: tokens.spacingHorizontalXS,
  spacingHorizontalSNudge: tokens.spacingHorizontalSNudge,
  spacingHorizontalS: tokens.spacingHorizontalS,
  spacingHorizontalMNudge: tokens.spacingHorizontalMNudge,
  spacingHorizontalM: tokens.spacingHorizontalM,
  spacingHorizontalL: tokens.spacingHorizontalL,
  spacingHorizontalXL: tokens.spacingHorizontalXL,
  spacingHorizontalXXL: tokens.spacingHorizontalXXL,
  spacingHorizontalXXXL: tokens.spacingHorizontalXXXL,

  // Vertical spacing
  spacingVerticalNone: tokens.spacingVerticalNone,
  spacingVerticalXXS: tokens.spacingVerticalXXS,
  spacingVerticalXS: tokens.spacingVerticalXS,
  spacingVerticalSNudge: tokens.spacingVerticalSNudge,
  spacingVerticalS: tokens.spacingVerticalS,
  spacingVerticalMNudge: tokens.spacingVerticalMNudge,
  spacingVerticalM: tokens.spacingVerticalM,
  spacingVerticalL: tokens.spacingVerticalL,
  spacingVerticalXL: tokens.spacingVerticalXL,
  spacingVerticalXXL: tokens.spacingVerticalXXL,
  spacingVerticalXXXL: tokens.spacingVerticalXXXL,
};

// Typography tokens - Use these for consistent text styling
export const typographyTokens = {
  // Font families
  fontFamilyBase: tokens.fontFamilyBase,
  fontFamilyMonospace: tokens.fontFamilyMonospace,
  fontFamilyNumeric: tokens.fontFamilyNumeric,

  // Font weights
  fontWeightRegular: tokens.fontWeightRegular,
  fontWeightMedium: tokens.fontWeightMedium,
  fontWeightSemibold: tokens.fontWeightSemibold,
  fontWeightBold: tokens.fontWeightBold,

  // Font sizes
  fontSizeBase100: tokens.fontSizeBase100,
  fontSizeBase200: tokens.fontSizeBase200,
  fontSizeBase300: tokens.fontSizeBase300,
  fontSizeBase400: tokens.fontSizeBase400,
  fontSizeBase500: tokens.fontSizeBase500,
  fontSizeBase600: tokens.fontSizeBase600,
  fontSizeHero700: tokens.fontSizeHero700,
  fontSizeHero800: tokens.fontSizeHero800,
  fontSizeHero900: tokens.fontSizeHero900,
  fontSizeHero1000: tokens.fontSizeHero1000,

  // Line heights
  lineHeightBase100: tokens.lineHeightBase100,
  lineHeightBase200: tokens.lineHeightBase200,
  lineHeightBase300: tokens.lineHeightBase300,
  lineHeightBase400: tokens.lineHeightBase400,
  lineHeightBase500: tokens.lineHeightBase500,
  lineHeightBase600: tokens.lineHeightBase600,
  lineHeightHero700: tokens.lineHeightHero700,
  lineHeightHero800: tokens.lineHeightHero800,
  lineHeightHero900: tokens.lineHeightHero900,
  lineHeightHero1000: tokens.lineHeightHero1000,

  // Semantic text styles - Use these for consistent text hierarchy
  caption1Regular: {
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
  },
  caption1Medium: {
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightMedium,
    lineHeight: tokens.lineHeightBase200,
  },
  body1Regular: {
    fontSize: tokens.fontSizeBase300,
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase300,
  },
  body1Medium: {
    fontSize: tokens.fontSizeBase300,
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightMedium,
    lineHeight: tokens.lineHeightBase300,
  },
};

// Border radius tokens - Use these for consistent rounded corners
export const borderRadiusTokens = {
  borderRadiusNone: tokens.borderRadiusNone,
  borderRadiusSmall: tokens.borderRadiusSmall,
  borderRadiusMedium: tokens.borderRadiusMedium,
  borderRadiusLarge: tokens.borderRadiusLarge,
  borderRadiusXLarge: tokens.borderRadiusXLarge,
  borderRadiusCircular: tokens.borderRadiusCircular,
  // Custom Forms-specific token for cards
  borderRadiusFormSpecial: '16px',
};

// Responsive breakpoint tokens - Use these for consistent responsive design
export const breakpointTokens = {
  // Mobile-first breakpoints following Fluent UI design patterns
  mobile: '480px',        // Small mobile devices
  tablet: '768px',        // Tablets and large mobile devices
  desktop: '1024px',      // Desktop and laptop screens
  desktopLarge: '1200px', // Large desktop screens
  desktopXL: '1366px',    // Extra large desktop screens
  desktopXXL: '1920px',   // Ultra-wide and high-resolution screens
};

// Component size tokens - Use these for consistent component dimensions
export const sizeTokens = {
  buttonMinWidth: '32px', // 32px - minimum width for ribbon buttons
  buttonHeight: '32px', // 32px - standard height for ribbon buttons
  controlHeight: '28px', // 28px - standard control height
  iconSmall: '16px', // Small icons
  iconMedium: '20px', // Standard icons
  iconLarge: '24px', // Large icons
  dividerWidth: '1px', // Divider line width
  dividerHeight: tokens.spacingHorizontalXL, // 24px - divider height
};

// Shadow tokens - Use these for consistent elevation
export const shadowTokens = {
  shadow2: tokens.shadow2,
  shadow4: tokens.shadow4,
  shadow8: tokens.shadow8,
  shadow16: tokens.shadow16,
  shadow28: tokens.shadow28,
  shadow64: tokens.shadow64,
};

// Animation tokens - Use these for consistent motion
export const animationTokens = {
  // Durations
  durationUltraFast: tokens.durationUltraFast,
  durationFaster: tokens.durationFaster,
  durationFast: tokens.durationFast,
  durationNormal: tokens.durationNormal,
  durationGentle: tokens.durationGentle,
  durationSlow: tokens.durationSlow,
  durationSlower: tokens.durationSlower,
  durationUltraSlow: tokens.durationUltraSlow,

  // Curves
  curveAccelerateMax: tokens.curveAccelerateMax,
  curveAccelerateMid: tokens.curveAccelerateMid,
  curveAccelerateMin: tokens.curveAccelerateMin,
  curveDecelerateMax: tokens.curveDecelerateMax,
  curveDecelerateMid: tokens.curveDecelerateMid,
  curveDecelerateMin: tokens.curveDecelerateMin,
  curveEasyEaseMax: tokens.curveEasyEaseMax,
  curveEasyEase: tokens.curveEasyEase,
  curveLinear: tokens.curveLinear,
};

// Data Visualization tokens - For charts, graphs, and data representations
export const dataVisualizationTokens = {
  // Primary 10-color qualitative palette (for categorical data)
  qualitative: {
    slot1: '#637CEF',  // Cornflower blue
    slot2: '#E3008C',  // Hot pink
    slot3: '#2AA0A4',  // Teal
    slot4: '#9373C0',  // Orchid purple
    slot5: '#13A10E',  // Light green
    slot6: '#3A96DD',  // Light blue
    slot7: '#CA5010',  // Pumpkin orange
    slot8: '#57811B',  // Lime green
    slot9: '#B146C2',  // Lilac
    slot10: '#AE8C00', // Gold
  },

  // Extended 5-color palette (for additional categorical needs)
  qualitativeExtended: {
    slot11: '#3C51B4', // Cornflower shade 20 (Web Light) / Cornflower tint 30 (Web Dark)
    slot12: '#AD006A', // Hot pink shade 20 (Web Light) / Hot pink tint 30 (Web Dark)
    slot13: '#026467', // Teal shade 20 (Web Light) / Teal tint 30 (Web Dark)
    slot14: '#674C8C', // Orchid shade 20 (Web Light) / Orchid tint 20 (Web Dark)
    slot15: '#0E7A0B', // Light green shade 20 (Web Light) / Light green tint 10 (Web Dark)
    slot16: '#2C72A8', // Light blue shade 20 (Web Light) / Light blue tint 10 (Web Dark)
    slot17: '#9A3D0C', // Pumpkin shade 20 (Web Light) / Pumpkin tint 20 (Web Dark)
    slot18: '#405F14', // Lime shade 30 (Web Light) / Lime primary (Web Dark)
    slot19: '#863593', // Lilac shade 20 (Web Light) / Lilac tint 20 (Web Dark)
    slot20: '#6D5700', // Gold shade 30 (Web Light) / Gold tint 20 (Web Dark)
    slot21: '#4F6BED', // Cornflower primary
    slot22: '#EA38A6', // Hot pink tint 20
    slot23: '#038387', // Teal primary
    slot24: '#8764B8', // Orchid primary
    slot25: '#11910D', // Light green shade 10 (Web Light) / Light green tint 10 (Web Dark)
    slot26: '#3487C7', // Light blue shade 10
    slot27: '#D06228', // Pumpkin tint 10
    slot28: '#689920', // Lime shade 10
    slot29: '#BA58C9', // Lilac tint 10
    slot30: '#937700', // Gold shade 20 (Web Light) / Gold primary (Web Dark)
    slot31: '#2C3C85', // Cornflower shade 30 (Web Light) / Cornflower tint 40 (Web Dark)
    slot32: '#7F004E', // Hot pink shade 30 (Web Light) / Hot pink tint 40 (Web Dark)
    slot33: '#02494C', // Teal shade 30 (Web Light) / Teal tint 40 (Web Dark)
    slot34: '#4C3867', // Orchid shade 30 (Web Light) / Orchid tint 30 (Web Dark)
    slot35: '#0B5A08', // Light green shade 30 (Web Light) / Light green tint 40 (Web Dark)
    slot36: '#20547C', // Light blue shade 30 (Web Light) / Light blue tint 30 (Web Dark)
    slot37: '#712D09', // Pumpkin shade 30 (Web Light) / Pumpkin tint 30 (Web Dark)
    slot38: '#23330B', // Lime shade 40 (Web Light) / Lime tint 30 (Web Dark)
    slot39: '#63276D', // Lilac shade 30 (Web Light) / Lilac tint 30 (Web Dark)
    slot40: '#3A2F00', // Gold shade 40 (Web Light) / Gold tint 30 (Web Dark)
  },

  // Sequential color scales (for continuous data)
  sequential: {
    // Cornflower blue scale (10 shades - lightest to darkest)
    cornflower: {
      tint50: '#E1E6FC',
      tint40: '#C8D1FA',
      tint30: '#93A4F4',
      tint20: '#778DF1',
      tint10: '#637CEF',
      primary: '#4F6BED',
      shade10: '#4760D5',
      shade20: '#3C51B4',
      shade30: '#2C3C85',
      shade40: '#182047',
    },
    // Hot pink scale (10 shades)
    hotPink: {
      tint50: '#FBD2EB',
      tint40: '#F7ADDA',
      tint30: '#EE5FB7',
      tint20: '#EA38A6',
      tint10: '#E61C99',
      primary: '#E3008C',
      shade10: '#CC007E',
      shade20: '#AD006A',
      shade30: '#7F004E',
      shade40: '#44002A',
    },
    // Teal scale
    teal: {
      tint40: '#9BD9DB',
      tint30: '#4CB4B7',
      tint20: '#2AA0A4',
      primary: '#038387',
      shade20: '#026467',
      shade30: '#02494C',
    },
    // Green scale
    green: {
      tint40: '#A7E3A5',
      tint30: '#54B054',
      tint10: '#27AC22',
      primary: '#13A10E',
      shade10: '#11910D',
      shade20: '#0E7A0B',
      shade30: '#0B5A08',
    },
    // Orange/Pumpkin scale
    orange: {
      tint30: '#DF8E64',
      tint20: '#D77440',
      tint10: '#D06228',
      primary: '#CA5010',
      shade10: '#B6480E',
      shade20: '#9A3D0C',
      shade30: '#712D09',
    },
  },

  // Diverging color scales (for data that diverges from a midpoint)
  diverging: {
    // 5-color diverging: Negative (red) to Positive (green)
    redToGreen5: ['#712D09', '#CA5010', '#E8E8E8', '#13A10E', '#0B5A08'],
    // 7-color diverging: Negative to Positive with neutral middle
    redToGreen7: ['#712D09', '#9A3D0C', '#CA5010', '#E8E8E8', '#13A10E', '#0E7A0B', '#0B5A08'],
    // 9-color diverging: Full spectrum
    redToGreen9: ['#712D09', '#9A3D0C', '#CA5010', '#D77440', '#E8E8E8', '#27AC22', '#13A10E', '#0E7A0B', '#0B5A08'],
  },

  // Threat/Status colors (for gauges, meters, and status indicators)
  threat: {
    // 3-color threat scale
    critical: '#712D09',  // Dark orange/red
    warning: '#CA5010',   // Pumpkin orange
    safe: '#13A10E',      // Light green
    // 5-color threat scale
    severe: '#9A3D0C',    // Orange shade 20
    moderate: '#D77440',  // Orange tint 20
    elevated: '#E8E8E8',  // Neutral gray
    low: '#27AC22',       // Green tint 10
    minimal: '#0E7A0B',   // Green shade 20
  },

  // Semantic data colors
  semantic: {
    positive: '#13A10E',  // Green - success, growth, positive trends
    negative: '#CA5010',  // Orange - warnings, decline, negative trends
    neutral: '#E8E8E8',   // Gray - neutral, no change
    info: '#3A96DD',      // Light blue - informational
    unknown: '#949494',   // Gray - unknown status
  },

  // Chart-specific colors
  chart: {
    // For bar charts, line charts, area charts
    primaryBar: '#637CEF',
    secondaryBar: '#E3008C',
    // For scatter plots, bubble charts
    primaryBubble: '#2AA0A4',
    secondaryBubble: '#9373C0',
    // For heatmaps
    heatmapLow: '#C8D1FA',
    heatmapMid: '#4F6BED',
    heatmapHigh: '#2C3C85',
  },

  // Teams-specific data visualization colors (Web Light)
  // Note: These have different values in Web Dark mode
  teams: {
    slot1: '#2F2F4A',   // Cornflower shade 40
    slot2: '#444791',   // Cornflower shade 20
    slot3: '#4F52B2',   // Cornflower shade 10
    slot4: '#5B5FC7',   // Cornflower primary
    slot5: '#242424',   // Neutral gray
    slot6: '#3D3D3D',   // Neutral gray (lighter)
    slot7: '#424242',   // Neutral gray (lighter)
    slot8: '#AE8C00',   // Gold shade 10
    slot9: '#3E2D3B',   // Purple shade
    slot10: '#4F222B',  // Hot pink shade 20
    slot11: '#943670',  // Orchid/magenta
    slot12: '#B4009E',  // Lilac/magenta
  },

  // Diverging threat colors (for status, threat levels)
  threatDiverging: {
    // Web Light values
    light: {
      critical: '#C4314B',  // Cranberry
      warning: '#CC4A31',   // Orange
      safe: '#5B5FC7',      // Cornflower primary
      safeAlt: '#444791',   // Cornflower shade 20
      disabled: '#E0E0E0',  // Grey 86
    },
    // Web Dark values
    dark: {
      critical: '#F87286',  // Cranberry tint 10
      warning: '#EC875F',   // Orange tint 10
      safe: '#9299F7',      // Cornflower tint 30
      safeAlt: '#7F85F5',   // Cornflower tint 20
      disabled: '#424242',  // Grey 30
    },
  },
};

// Export all tokens as a single object for convenience
export const fluentTokens = {
  colors: colorTokens,
  spacing: spacingTokens,
  typography: typographyTokens,
  borderRadius: borderRadiusTokens,
  shadows: shadowTokens,
  animations: animationTokens,
  breakpoints: breakpointTokens,
  dataVisualization: dataVisualizationTokens,
};
